// Activity-log entries surfaced by `<bb-log>`.
//
// The widget collects three kinds of events into one ordered list:
//   - `http` — every relay call the bridge made (method, path, status, ms,
//     and the request/response bodies), reported via `makeBridgeFetch`'s
//     `onCall` hook. Click an entry to expand the bodies.
//   - `hint`  — the raw declarative `response.hint` the bot returned. (Bot
//     hints are surfaced here, not in the chat panels.) Click to expand.
//   - `note`  — client-side milestones (conversation started, item selected
//     from the menu, order submitted).
//
// This mirrors what demo-dev showed when its "Show system messages" checkbox
// was on, but as a first-class panel instead of inline chat bubbles.

import type { Hint } from './types'

export interface HttpLogEntry {
  kind: 'http'
  id: number
  at: number
  method: string
  url: string
  status: number
  ms: number
  ok: boolean
  requestBody?: string
  responseBody?: string
}

export interface HintLogEntry {
  kind: 'hint'
  id: number
  at: number
  hint: Hint
}

export interface NoteLogEntry {
  kind: 'note'
  id: number
  at: number
  subject: string
  text: string
}

export type LogEntry = HttpLogEntry | HintLogEntry | NoteLogEntry

// What callers hand to the widget; `id` + `at` are stamped on append.
export type LogEntryInput =
  | Omit<HttpLogEntry, 'id' | 'at'>
  | Omit<HintLogEntry, 'id' | 'at'>
  | Omit<NoteLogEntry, 'id' | 'at'>

// Ring-buffer cap so a long session can't grow the list without bound.
export const MAX_LOG_ENTRIES = 500

export type StatusTone = 'ok' | 'warn' | 'err'

export function statusTone(status: number): StatusTone {
  if (status === 0 || status >= 500) return 'err'
  if (status >= 400) return 'warn'
  return 'ok'
}

// One-line summary rendered in the panel (the `kind`/method label is added
// separately by the panel).
export function summariseLog(e: LogEntry): string {
  switch (e.kind) {
    case 'http':
      return `${e.url} → ${e.status || 'ERR'} (${e.ms}ms)`
    case 'hint':
      return `${e.hint.type}/${e.hint.subject}`
    case 'note':
      return `${e.subject} — ${e.text}`
  }
}

// Pretty-print a body string if it parses as JSON, else return it as-is.
function prettyBody(s: string): string {
  try {
    return JSON.stringify(JSON.parse(s), null, 2)
  } catch {
    return s
  }
}

// The expandable detail block for a log entry (`null` ⇒ not expandable).
export function logDetail(e: LogEntry): string | null {
  switch (e.kind) {
    case 'hint':
      return JSON.stringify(e.hint, null, 2)
    case 'http': {
      const parts: string[] = []
      if (e.requestBody) parts.push(`→ request\n${prettyBody(e.requestBody)}`)
      if (e.responseBody) parts.push(`← response\n${prettyBody(e.responseBody)}`)
      return parts.length ? parts.join('\n\n') : null
    }
    case 'note':
      return null
  }
}
